/**
 * StrategyMatchPanel — "Your Strategy Match: 94/100" breakdown card.
 * Renders the deterministic criteria evaluation (pass/partial/fail per
 * criterion) computed by strategy_match (backend or JS mirror).
 */
import { Target, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";

const STATUS_CFG = {
  pass: { Icon: CheckCircle2, cls: "text-emerald-500" },
  partial: { Icon: AlertTriangle, cls: "text-amber-500" },
  fail: { Icon: XCircle, cls: "text-red-500" },
};

const VERDICTS = {
  strong_match: { label: "Strong Match", cls: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" },
  partial_match: { label: "Partial Match", cls: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  weak_match: { label: "Weak Match", cls: "bg-red-500/10 text-red-500 border-red-500/20" },
};

export const StrategyMatchPanel = ({ match, strategyName }) => {
  if (!match) return null;
  const score = match.match_score ?? 0;
  const verdict = VERDICTS[match.verdict] || VERDICTS.weak_match;
  const scoreColor = score >= 80 ? "text-emerald-500" : score >= 55 ? "text-amber-500" : "text-red-500";
  const barColor = score >= 80 ? "bg-emerald-500" : score >= 55 ? "bg-amber-500" : "bg-red-500";

  return (
    <Card className="bg-secondary/20 border-border" data-testid="strategy-match-panel">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold flex items-center gap-1.5">
            <Target className="w-4 h-4 text-amber-500" />
            Your Strategy Match
            {strategyName && <span className="text-xs text-muted-foreground font-normal">· {strategyName}</span>}
          </h4>
          <div className="flex items-center gap-2">
            <Badge className={`text-[10px] border ${verdict.cls}`} data-testid="strategy-match-verdict">{verdict.label}</Badge>
            <span className={`font-mono text-lg font-bold ${scoreColor}`} data-testid="strategy-match-score">
              {score}<span className="text-xs text-muted-foreground font-normal">/100</span>
            </span>
          </div>
        </div>
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mb-3">
          <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${score}%` }} />
        </div>
        <div className="space-y-1.5" data-testid="strategy-match-breakdown">
          {(match.breakdown || []).map((row) => {
            const cfg = STATUS_CFG[row.status] || STATUS_CFG.fail;
            const { Icon } = cfg;
            return (
              <div key={row.criterion} className="flex items-start gap-2" data-testid={`match-row-${row.criterion}`}>
                <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${cfg.cls}`} />
                <div className="min-w-0">
                  <span className="text-xs font-medium text-foreground">{row.label}</span>
                  <span className="text-xs text-muted-foreground"> — {row.detail}</span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 italic">
          Deal Score measures deal quality. Strategy Match measures fit against YOUR saved criteria — a great deal can still be a poor fit.
        </p>
      </CardContent>
    </Card>
  );
};

export default StrategyMatchPanel;
